import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import {
  useTableRowStyles,
  useDeleteButtonStyles,
} from '../../styles/TableStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import Grid from '@material-ui/core/Grid';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import DeleteIcon from '@material-ui/icons/Delete';
import LockIcon from '@material-ui/icons/Lock';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { UserChanges, UserStatus } from './EditGroupMembershipsDialog';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import UndoIcon from '@material-ui/icons/Undo';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
  }),
);

interface tableProps {
  usersToAdd: UserChanges[];
  deleteUser: (users: UserChanges) => void;
}
export default function EnhancedTable(props: tableProps) {
  const classes = useStyles();
  const myClasses = useTableRowStyles();
  const deleteButtonStyleClasses = useDeleteButtonStyles();
  const { usersToAdd, deleteUser } = props;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, _] = React.useState(10);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleDelete = (user: UserChanges) => {
    if (user.status === UserStatus.REMOVE) {
      const updated = { ...user, status: UserStatus.KEEP };
      deleteUser(updated);
    } else {
      deleteUser(user);
    }
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, usersToAdd.length - page * rowsPerPage);

  const userEntry = (row: UserChanges) => {
    const displayName = row.user.spec.profile!.displayName;
    switch (row.status) {
      case UserStatus.REMOVE: {
        return (
          <Grid container direction="row" alignItems="center">
            {displayName}
            &nbsp;
            <RemoveCircleOutlineIcon />
          </Grid>
        );
      }
      case UserStatus.ADD: {
        return (
          <Grid container direction="row" alignItems="center">
            {displayName}
            &nbsp;
            <AddCircleOutlineIcon />
          </Grid>
        );
      }
      case UserStatus.DERIVED: {
        return (
          <Grid container direction="row" alignItems="center">
            {displayName}
            &nbsp;
            <Tooltip title="In the group because the department names this person as a contact. Change the contact to change this.">
              <LockIcon fontSize="small" />
            </Tooltip>
          </Grid>
        );
      }
      default: {
        return (
          <Grid container direction="row" alignItems="center">
            {displayName}
          </Grid>
        );
      }
    }
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table
            aria-labelledby="tableTitle"
            size={'small'}
            aria-label="enhanced table"
          >
            <TableBody>
              {usersToAdd
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, _) => {
                  let stylesToApplyToRoot = myClasses.tableRowRootDefault;
                  switch (row.status) {
                    case UserStatus.REMOVE: {
                      stylesToApplyToRoot = myClasses.tableRowRootRemoved;
                      break;
                    }
                    case UserStatus.ADD: {
                      stylesToApplyToRoot = myClasses.tableRowRootAdded;
                      break;
                    }
                    default: {
                      stylesToApplyToRoot = myClasses.tableRowRootDefault;
                      break;
                    }
                  }

                  return (
                    <TableRow
                      key={row.user.spec.profile?.email}
                      classes={{ root: stylesToApplyToRoot }}
                      style={{ height: 52 }}
                    >
                      <TableCell component="th" scope="row">
                        {userEntry(row)}
                      </TableCell>
                      <TableCell style={{ width: 80 }} align="right">
                        {/*
                          No button at all for a derived member rather than a
                          disabled one: there is nothing to press here, and a
                          greyed out bin invites the try.
                        */}
                        {row.status !== UserStatus.DERIVED && (
                          <IconButton
                            aria-label={
                              row.status === UserStatus.REMOVE
                                ? 'undo'
                                : 'delete'
                            }
                            onClick={() => handleDelete(row)}
                            className={deleteButtonStyleClasses.padding}
                          >
                            {row.status === UserStatus.REMOVE ? (
                              <UndoIcon fontSize="small" />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {[...Array(emptyRows).keys()].map((_, i) => {
                return (
                  <TableRow
                    key={i}
                    classes={{ root: myClasses.tableRowRootDefault }}
                    style={{ height: 52 }}
                  >
                    <TableCell component="th" scope="row"></TableCell>
                    <TableCell style={{ width: 80 }} align="right"></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={usersToAdd.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          page={page}
          onPageChange={handleChangePage}
        />
      </Paper>
    </div>
  );
}
